import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Checkbox } from '../../components/form/Checkbox';
import { FileUploader } from '../../components/ui/FileUploader/FileUploader';
import { SignaturePad } from '../../components/ui/SignaturePad/SignaturePad';
import { PageLoader } from '../../components/ui/PageLoader/PageLoader';
import { useToast } from '../../hooks/useToast';
import { useBookingDetail } from '../bookings/hooks/useBookingDetail';
import {
  useCreateReceipt,
  useUploadReceiptPhotos,
} from './hooks/useReceiptMutations';
import type { CreateReceiptInput, ReceiptDetailResponse } from '../../types/api';
import styles from './ReceiptDirectCreatePage.module.css';

interface FormState {
  car_color: string;
  car_year: string;
  car_mileage: string;
  chassis: string;
  has_mot: boolean;
  has_keys: boolean;
  has_logbook: boolean;
  cheque_number: string;
  notes: string;
}

const today = new Date().toISOString().slice(0, 10);

const INITIAL: FormState = {
  car_color: '',
  car_year: '',
  car_mileage: '',
  chassis: '',
  has_mot: false,
  has_keys: false,
  has_logbook: false,
  cheque_number: '',
  notes: '',
};

export function ReceiptFromBookingPage(): JSX.Element {
  const { enc_id } = useParams<{ enc_id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const booking = useBookingDetail(enc_id);

  const [form, setForm] = useState<FormState>(INITIAL);
  const [photos, setPhotos] = useState<File[]>([]);
  const [sellerSig, setSellerSig] = useState<string | null>(null);
  const [buyerSig, setBuyerSig] = useState<string | null>(null);

  const createMutation = useCreateReceipt();
  const uploadPhotos = useUploadReceiptPhotos();

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    if (booking.data?.booking.notes && !form.notes) {
      setForm((prev) => ({ ...prev, notes: booking.data!.booking.notes ?? '' }));
    }
  }, [booking.data, form.notes]);

  const errors = useMemo(() => {
    const e: string[] = [];
    if (!booking.data) e.push('Booking not loaded.');
    return e;
  }, [booking.data]);

  const canSubmit =
    !!booking.data &&
    errors.length === 0 &&
    !createMutation.isPending &&
    !uploadPhotos.isPending;

  async function handleSubmit() {
    if (!canSubmit || !booking.data) return;
    const b = booking.data.booking;

    const input: CreateReceiptInput = {
      customer_uuid: b.customer_uuid ?? undefined,
      driver_uuid: undefined,
      receipt_date: today,
      car_registration: b.car_registration,
      car_make_model: b.car_make_model,
      car_color: form.car_color.trim() || undefined,
      car_year: form.car_year ? Number(form.car_year) : undefined,
      car_mileage: form.car_mileage ? Number(form.car_mileage) : undefined,
      chassis: form.chassis.trim() || undefined,
      has_mot: form.has_mot,
      has_keys: form.has_keys,
      has_logbook: form.has_logbook,
      customer_name: b.name,
      customer_email: b.email ?? null,
      customer_phone: b.phone,
      customer_address: b.address,
      customer_postcode: b.postcode,
      collection_address: b.collection_address ?? undefined,
      collection_postcode: b.collection_postcode ?? undefined,
      amount: b.price,
      cheque_number: form.cheque_number.trim() || undefined,
      notes: form.notes.trim() || null,
      declaration_accepted: true,
    };

    if (sellerSig) input.seller_signature = sellerSig;
    if (buyerSig) input.buyer_signature = buyerSig;

    try {
      const res = await createMutation.mutateAsync(input);
      const receipt = (res.data as ReceiptDetailResponse).receipt;

      if (photos.length > 0) {
        try {
          await uploadPhotos.mutateAsync({ encId: receipt.enc_id, files: photos });
        } catch {
          toast.warning(
            'Receipt created, but photos failed to upload. Try uploading from the detail page.',
          );
        }
      }

      toast.success('Receipt created from booking');
      navigate(`/collections/receipts/${encodeURIComponent(receipt.enc_id)}`);
    } catch {
      // toast handled
    }
  }

  if (!enc_id) return <PageLoader message="Loading…" />;
  if (booking.isLoading) return <PageLoader message="Loading booking…" />;
  if (booking.isError || !booking.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Booking not found" />
        <Card>
          <p>Could not load booking.</p>
        </Card>
      </div>
    );
  }

  const b = booking.data.booking;

  return (
    <div className={styles.page}>
      <PageHeader
        title="New Receipt from Booking"
        subtitle={`${b.unique_id} · ${b.car_registration} · ${b.name}`}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate(`/collections/bookings/${encodeURIComponent(enc_id)}`)}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
        }
      />

      <Card title="Booking summary">
        <div className={styles.grid2}>
          <div>
            <strong>{b.name}</strong>
            <p>{b.phone}</p>
            <p>{b.email ?? '—'}</p>
          </div>
          <div>
            <strong>
              {b.car_registration} — {b.car_make_model}
            </strong>
            <p>{b.address}, {b.postcode}</p>
            <p>£{b.price.toFixed(2)}</p>
          </div>
        </div>
      </Card>

      <Card title="Vehicle condition">
        <div className={styles.grid2}>
          <Input
            label="Colour"
            value={form.car_color}
            onChange={(e) => setField('car_color', e.target.value)}
          />
          <Input
            label="Year"
            variant="number"
            value={form.car_year}
            onChange={(e) => setField('car_year', e.target.value)}
          />
          <Input
            label="Mileage"
            variant="number"
            value={form.car_mileage}
            onChange={(e) => setField('car_mileage', e.target.value)}
          />
          <Input
            label="Chassis"
            value={form.chassis}
            onChange={(e) => setField('chassis', e.target.value)}
          />
          <Input
            label="Cheque number"
            value={form.cheque_number}
            onChange={(e) => setField('cheque_number', e.target.value)}
          />
        </div>
        <div className={styles.checkboxRow} style={{ marginTop: '0.75rem' }}>
          <Checkbox
            label="MOT present"
            checked={form.has_mot}
            onChange={(e) => setField('has_mot', e.target.checked)}
          />
          <Checkbox
            label="Keys present"
            checked={form.has_keys}
            onChange={(e) => setField('has_keys', e.target.checked)}
          />
          <Checkbox
            label="Logbook present"
            checked={form.has_logbook}
            onChange={(e) => setField('has_logbook', e.target.checked)}
          />
        </div>
      </Card>

      <Card title="Photos (max 7)">
        <FileUploader value={photos} onChange={setPhotos} maxFiles={7} />
      </Card>

      <Card title="Signatures">
        <div className={styles.signatureGrid}>
          <SignaturePad label="Seller signature" value={sellerSig} onChange={setSellerSig} />
          <SignaturePad label="Buyer signature" value={buyerSig} onChange={setBuyerSig} />
        </div>
      </Card>

      <Card title="Notes">
        <textarea
          rows={3}
          style={{ width: '100%', resize: 'vertical' }}
          value={form.notes}
          onChange={(e) => setField('notes', e.target.value)}
          aria-label="Notes"
        />
      </Card>

      <div className={styles.submitBar}>
        <ul className={styles.errorList} aria-live="polite">
          {errors.slice(0, 4).map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
        <Button
          variant="primary"
          onClick={() => void handleSubmit()}
          loading={createMutation.isPending || uploadPhotos.isPending}
          disabled={!canSubmit}
        >
          Issue Receipt
        </Button>
      </div>
    </div>
  );
}
